import "../../i18n/i18n";
import { VehiclesTable } from "./tables/vehiclesTable";
import { useVehicles } from "./useVehicles";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { ButtonDS, Spacer, triggerToast } from "@qivia/ui";
import { useTranslation } from "react-i18next";
import {
  selectConnectVehiclesToProviderStatus,
  telemetrySlice,
} from "./telemetrySlice";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import styled from "styled-components";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import { VehicleDisplayed } from "./telemetryApi";
import { ConnectVehiclesToProviderModal } from "./components/connectVehiclesToProviderModal";

export const AddVehiclesToProviderTab = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const params = useParams();
  const vehiclesListWithId = useVehicles();
  const [selectedVehicles, setSelectedVehicles] = useState<VehicleDisplayed[]>(
    [],
  );
  const [openConnectionPlanModal, setOpenConnectionPlanModal] = useState(false);
  const connectVehiclesToProviderStatus = useAppSelector(
    selectConnectVehiclesToProviderStatus,
  );

  const filteredVehiclesListWithId = useMemo(() => {
    return vehiclesListWithId.filter(
      (v) =>
        (v.isEligible &&
          v.isEligible.toString() ===
            t("vehiclesOfACompany.column.isEligible.true") &&
          v.connectionStatus ===
            t("vehiclesOfACompany.column.connectionStatus.IDLE") &&
          !v.dataProvider) ||
        (v.connectionStatus ===
          t("vehiclesOfACompany.column.connectionStatus.REVOKED") &&
          v.dataProvider === "HIGH_MOBILITY"),
    );
  }, [t, vehiclesListWithId]);

  const onResetConnectionPlanModal = () => {
    setOpenConnectionPlanModal(false);
  };

  const checkVehiclesEligibilityAction = useMemo(() => {
    return (
      <ButtonDS
        text={t("telemetry.addVehiclesToProvider.button")}
        format="fill"
        buttonType="primary"
        onClick={() => setOpenConnectionPlanModal(true)}
        disabled={selectedVehicles.length === 0}
      />
    );
  }, [t, selectedVehicles.length]);

  useEffect(() => {
    if (connectVehiclesToProviderStatus === "success") {
      triggerToast(t("telemetry.addVehiclesToProvider.success") || "", "valid");
      void dispatch(telemetrySlice.actions.resetConnectVehiclesToProviderStatus());
      setSelectedVehicles([]);
    } else if (connectVehiclesToProviderStatus === "failed") {
      triggerToast(t("telemetry.addVehiclesToProvider.failure") || "", "error");
      void dispatch(telemetrySlice.actions.resetConnectVehiclesToProviderStatus());
    }
  }, [connectVehiclesToProviderStatus, t, dispatch]);

  if (params.tab !== "addVehiclesToProvider") {
    return;
  }

  return (
    <StyledContainer>
      <Spacer y={1} />
      <StyledDescription>
        {t("telemetry.addVehiclesToProvider.description")}
      </StyledDescription>
      <Spacer y={1} />
      <StyledDescription>
        {t("telemetry.addVehiclesToProvider.description2")}
      </StyledDescription>
      <Spacer y={1} />
      <StyledTableContainer>
        <VehiclesTable
          vehiclesListWithId={filteredVehiclesListWithId}
          pageName="vehiclesOfACompany"
          pageTitle="Add-to-provider"
          dataProvider="HIGH_MOBILITY"
          action={checkVehiclesEligibilityAction}
          rowsChecked={selectedVehicles}
          setRowsChecked={setSelectedVehicles}
        />
      </StyledTableContainer>
      <ConnectVehiclesToProviderModal
        visible={openConnectionPlanModal}
        onClose={onResetConnectionPlanModal}
        selectedVehicles={selectedVehicles}
      />
    </StyledContainer>
  );
};

const StyledDescription = styled.div`
  ${typographies["Header/H3"]}
`;

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-y: hidden;
`;

const StyledTableContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-y: auto;
`;